import {
  GetBookingMyMscResponse,
  MyMscExcursion,
  OBSI,
  OBSObjI,
} from '@msc-b2c/interfaces/interfaces';
import { BookingStatusEnum } from '@msc-b2c/interfaces/enums';
import { getIsoDatePart } from '@msc-b2c/shared-utils';
import { CartItem } from '@msc-b2c/interfaces/types';
import {
  areAllAddonEligiblePassengersBookedInCabin,
  getAddonEligiblePassengersInCabin,
  getAlreadyBookedPassengerId,
  getCabinPassengers,
  hasRemainingAddonEligiblePassengersInCabin,
  isNoEligiblePassengersForExcursion,
  isNoEligiblePassengersForOBS,
} from '../postbookingresults/utils/PostBookingResultsUtilities';
import {
  getCmsRelatedCodes,
  isAllInclusiveByCmsOrTypeCode,
  isCmsConfiguredAllInclusive,
} from '../postbookingresults/utils/allInclusiveCmsHelpers';

export type AlreadyPurchasedCardCtaMode = 'edit' | 'viewDetail' | 'hidden';

export const hasRemainingAddonPassengersForAlreadyPurchasedItem = (
  card: MyMscExcursion | OBSObjI | null | undefined,
  booking: GetBookingMyMscResponse | undefined,
  cabinIds: string[],
  isExcursion: boolean,
  cartItems?: CartItem[]
): boolean => {
  if (!card?.code || !booking || !cabinIds.length) {
    return false;
  }

  return cabinIds.some((cabinId) =>
    hasRemainingAddonEligiblePassengersInCabin(card, booking, cabinId, isExcursion, cartItems)
  );
};

const getBookedItemsForCode = (
  booking: GetBookingMyMscResponse,
  cruiseId: string,
  itemCode: string,
  isExcursion: boolean
) => {
  const prepaidBookings =
    booking.prepaid?.bookings?.filter((prepaidBooking) => prepaidBooking.cruise.id === cruiseId) ??
    [];

  if (isExcursion) {
    return prepaidBookings
      .flatMap((prepaidBooking) => prepaidBooking.excursions ?? [])
      .filter(
        (item) => item.code === itemCode && item.bookingStatus?.code === BookingStatusEnum.BOOKED
      );
  }

  return prepaidBookings
    .flatMap((prepaidBooking) => prepaidBooking.onboardServices ?? [])
    .filter(
      (item) => item.code === itemCode && item.bookingStatus?.code === BookingStatusEnum.BOOKED
    );
};

export const isAlreadyPurchasedItemPaymentFinalized = (
  booking: GetBookingMyMscResponse | undefined,
  cruiseId: string | undefined,
  itemCode: string | undefined,
  isExcursion: boolean
): boolean => {
  if (!booking || !cruiseId || !itemCode) {
    return false;
  }

  const bookedItems = getBookedItemsForCode(booking, cruiseId, itemCode, isExcursion);
  if (!bookedItems.length) {
    return false;
  }

  const prepaidBookings =
    booking.prepaid?.bookings?.filter((prepaidBooking) => prepaidBooking.cruise.id === cruiseId) ??
    [];

  return bookedItems.every((bookedItem) => {
    const parentBooking = prepaidBookings.find((prepaidBooking) => {
      const items = isExcursion
        ? prepaidBooking.excursions ?? []
        : prepaidBooking.onboardServices ?? [];
      return items.some((item) => item.id === bookedItem.id);
    });
    return parentBooking?.isConfirmed === true;
  });
};

export const isAllInclusiveAlreadyPurchasedItem = (
  card: MyMscExcursion | OBSObjI,
  allInclusiveCmsItems: Parameters<typeof isCmsConfiguredAllInclusive>[0],
  isExcursion: boolean
): boolean => {
  if (isExcursion) {
    return isCmsConfiguredAllInclusive(allInclusiveCmsItems, card.code);
  }

  const obsCard = card as OBSObjI;
  return (
    Boolean(obsCard.prepaidItems?.isAllInclusive) ||
    isAllInclusiveByCmsOrTypeCode(allInclusiveCmsItems, obsCard.code, obsCard.type?.code) ||
    getCmsRelatedCodes(allInclusiveCmsItems, obsCard.code).length > 0
  );
};

/** True when every add-on eligible passenger in the active cabin is already BOOKED on this item. */
export const areAllPassengersBookedForAlreadyPurchasedItem = (
  card: MyMscExcursion | OBSObjI,
  booking: GetBookingMyMscResponse | undefined,
  activeCabinId: string | undefined,
  isExcursion: boolean
): boolean => areAllAddonEligiblePassengersBookedInCabin(card, booking, activeCabinId, isExcursion);

/** True when the cabin has at least one booked passenger eligible for refund on this item. */
export const hasRefundablePassengersInCabinForAlreadyPurchased = (
  card: MyMscExcursion | OBSObjI,
  booking: GetBookingMyMscResponse | undefined,
  cabinId: string | undefined,
  isExcursion: boolean
): boolean => {
  if (!booking || !cabinId) {
    return false;
  }

  const bookedPassengerIds = getAlreadyBookedPassengerId(card, booking, isExcursion, true);

  return getCabinPassengers(booking, cabinId).some(
    (passenger) => passenger.id && bookedPassengerIds.includes(passenger.id)
  );
};

export const hasAddonEligiblePassengersForAlreadyPurchased = (
  card: MyMscExcursion | OBSObjI,
  shopExcursion: Parameters<typeof isNoEligiblePassengersForExcursion>[1],
  shopOBS: OBSI | null,
  activeCabinId: string | undefined,
  booking: GetBookingMyMscResponse | undefined,
  isExcursion: boolean
): boolean => {
  if (!booking) {
    return false;
  }

  return isExcursion
    ? !isNoEligiblePassengersForExcursion(
        card as MyMscExcursion,
        shopExcursion,
        activeCabinId,
        booking
      )
    : !isNoEligiblePassengersForOBS(card as OBSObjI, shopOBS, activeCabinId, booking);
};

export const getAlreadyPurchasedCardCtaMode = ({
  card,
  booking,
  cruiseId,
  activeCabinId,
  isExcursion,
  isRefundEnabled,
  isAllInclusiveItem,
  cartItems,
  shopExcursion,
  shopOBS,
}: {
  card: MyMscExcursion | OBSObjI;
  booking?: GetBookingMyMscResponse;
  cruiseId?: string;
  activeCabinId?: string;
  isExcursion: boolean;
  isRefundEnabled: boolean;
  isAllInclusiveItem: boolean;
  cartItems?: CartItem[];
  shopExcursion: Parameters<typeof isNoEligiblePassengersForExcursion>[1];
  shopOBS: OBSI | null;
}): AlreadyPurchasedCardCtaMode => {
  if (!booking || !card.code) {
    return 'hidden';
  }

  if (!isRefundEnabled && isAllInclusiveItem) {
    return 'viewDetail';
  }

  const bookedPassengerIds = getAlreadyBookedPassengerId(card, booking, isExcursion, true);
  const hasBookedPassengers = bookedPassengerIds.length > 0;
  const hasAddonEligible = isRefundEnabled
    ? hasAddonEligiblePassengersForAlreadyPurchased(
        card,
        shopExcursion,
        shopOBS,
        activeCabinId,
        booking,
        isExcursion
      )
    : hasRemainingAddonEligiblePassengersInCabin(
        card,
        booking,
        activeCabinId,
        isExcursion,
        cartItems
      );
  const addonEligiblePassengersInActiveCabin = getAddonEligiblePassengersInCabin(
    card,
    booking,
    activeCabinId,
    isExcursion
  );
  const allEligiblePassengersAccountedFor =
    !hasAddonEligible && addonEligiblePassengersInActiveCabin.length > 0;
  const paymentFinalized = isAlreadyPurchasedItemPaymentFinalized(
    booking,
    cruiseId,
    card.code,
    isExcursion
  );

  if (!isRefundEnabled && allEligiblePassengersAccountedFor) {
    return 'viewDetail';
  }

  if (paymentFinalized && hasBookedPassengers && !hasAddonEligible) {
    return isRefundEnabled ? 'edit' : 'viewDetail';
  }

  if (!hasAddonEligible && !isRefundEnabled) {
    return hasBookedPassengers ? 'viewDetail' : 'hidden';
  }

  if (hasAddonEligible || isRefundEnabled) {
    return 'edit';
  }

  return hasBookedPassengers ? 'viewDetail' : 'hidden';
};

export const hasPassengerSelection = (
  selection: Record<string, Record<string, boolean>> | null | undefined
): boolean =>
  !!selection &&
  Object.values(selection).some((cabinPassengers) => Object.values(cabinPassengers).some(Boolean));

/** True when at least one checked passenger is not in excludePassengerIds (e.g. cart-prefilled only). */
export const hasPassengerSelectionExcludingPassengerIds = (
  selection: Record<string, Record<string, boolean>> | null | undefined,
  excludePassengerIds: Set<string>
): boolean =>
  !!selection &&
  Object.entries(selection).some(([, cabinPassengers]) =>
    Object.entries(cabinPassengers).some(
      ([passengerId, isChecked]) => isChecked && !excludePassengerIds.has(passengerId)
    )
  );

export const createEmptyPassengerSelection = (
  booking: GetBookingMyMscResponse | undefined,
  cruiseId: string | undefined,
  activeCabinId: string | undefined
): Record<string, Record<string, boolean>> => {
  if (!booking || !cruiseId) {
    return {};
  }

  const cabinIds =
    booking.cabins
      ?.filter((cabin) => cabin.cruise?.id === cruiseId)
      .map((cabin) => cabin.id)
      .filter((id): id is string => Boolean(id)) ?? [];

  const scopedCabinIds = activeCabinId ? [activeCabinId] : cabinIds;

  return scopedCabinIds.reduce<Record<string, Record<string, boolean>>>((acc, cabinId) => {
    acc[cabinId] = {};
    return acc;
  }, {});
};

export type ItineraryDayDetailsByDate = {
  dayNumber?: number;
  portName?: string;
  portCode?: string;
  isAtSea: boolean;
};

export const normalizePurchasedExcursionDate = (value?: string): string =>
  value?.trim() ? getIsoDatePart(value) : '';

/** Compares excursion start dates from prepaid booking vs catalog/card (handles ISO and space-separated formats). */
export const hasSameExcursionStartDate = (leftDate?: string, rightDate?: string): boolean => {
  if (!leftDate || !rightDate) {
    return true;
  }

  const normalizedLeft = normalizePurchasedExcursionDate(leftDate) || leftDate;
  const normalizedRight = normalizePurchasedExcursionDate(rightDate) || rightDate;

  return normalizedLeft === normalizedRight;
};

export const calculatePurchasedExcursionDayNumber = (
  excursionDate: string | undefined,
  departureDate: string | undefined
): number | undefined => {
  const normalizedExcursionDate = normalizePurchasedExcursionDate(excursionDate);
  const normalizedDepartureDate = normalizePurchasedExcursionDate(departureDate);

  if (!normalizedExcursionDate || !normalizedDepartureDate) {
    return undefined;
  }

  const excursionTime = Date.parse(`${normalizedExcursionDate}T00:00:00`);
  const departureTime = Date.parse(`${normalizedDepartureDate}T00:00:00`);

  if (Number.isNaN(excursionTime) || Number.isNaN(departureTime)) {
    return undefined;
  }

  return Math.round((excursionTime - departureTime) / (1000 * 60 * 60 * 24)) + 1;
};

export const resolveExcursionPreTitleFields = (
  excursion: Pick<MyMscExcursion, 'day' | 'portCode' | 'startDate'>,
  itineraryDayDetailsByDate: Map<string, ItineraryDayDetailsByDate>,
  departureDate?: string
): Pick<MyMscExcursion, 'day' | 'portCode' | 'startDate'> => {
  const normalizedStartDate = normalizePurchasedExcursionDate(excursion.startDate);
  const dateDetails = normalizedStartDate
    ? itineraryDayDetailsByDate.get(normalizedStartDate)
    : undefined;

  return {
    day:
      excursion.day ??
      dateDetails?.dayNumber ??
      calculatePurchasedExcursionDayNumber(normalizedStartDate, departureDate),
    portCode: excursion.portCode ?? dateDetails?.portCode,
    startDate: normalizedStartDate || excursion.startDate,
  };
};
